import api from './api';

/**
 * #691: Reconciliation > Overview. Cards, tables and exception queues for one month (and optionally one
 * bank account). Amounts are API decimal strings. Every card count is the row count of a queue.
 */

const BASE = '/commissions/reconciliation-dashboard';

export type DashQueue = 'unmatched_credits' | 'unmatched_debits' | 'payouts_not_in_bank' | 'short_excess';
export type DashTable = 'insurers' | 'posps' | 'accounts';

export const QUEUE_TITLE: Record<DashQueue, string> = {
  unmatched_credits: 'Unmatched bank credits',
  unmatched_debits: 'Unmatched bank debits',
  payouts_not_in_bank: 'Payouts not in the bank',
  short_excess: 'Short or excess insurer payments',
};

export interface CountAmount {
  count: number;
  amount: string;
}
export interface BankCard {
  matched: CountAmount;
  unmatched: CountAmount;
  ignored: CountAmount;
}

export interface InsurerRow {
  insurer: string;
  insurer_name: string;
  expected: string;
  received: string;
  tds: string;
  outstanding: string;
  received_no_figure: string;
  short: string;
  excess: string;
  on_account: string;
  unmatched_credits: number;
  oldest_unmatched_credit: string | null;
}
export interface PospRow {
  posp: string;
  posp_code: string;
  posp_name: string;
  earned: string;
  paid: string;
  unpaid: string;
  ledger_paid: string;
  difference: string;
  unconfirmed_payouts: number;
}
export interface AccountRow {
  bank_account: string;
  bank_account_name: string;
  statement_closing: string | null;
  statement_date: string | null;
  book_balance: string;
  difference: string | null;
  sign_off: string;
}

export interface SignOff {
  id?: string;
  status: 'open' | 'in_progress' | 'signed_off' | 'draft' | 'completed';
  prepared_by: string;
  approved_by: string;
  approved_at: string | null;
  notes: string;
}

export interface DashboardSummary {
  month: string;
  bank_account: string;
  cards: {
    bank_credits: BankCard;
    bank_debits: BankCard;
    insurer_receivable: { expected: string; received: string; tds: string; outstanding: string; on_account: string };
    posp_payable: { earned: string; paid: string; unpaid: string; unconfirmed_payouts: number };
    unexplained: { total: string; insurer_short: string; insurer_excess: string; posp_paid_not_proved: string; count: number };
    balance: {
      statement_closing: string | null;
      book_balance: string | null;
      difference: string | null;
      accounts_without_statement: number;
    };
  };
  queues: Record<DashQueue, number>;
  tables: { insurers: InsurerRow[]; posps: PospRow[]; accounts: AccountRow[] };
  sign_off: SignOff | null;
}

export interface QueueData {
  name: DashQueue;
  title: string;
  columns: string[];
  rows: Record<string, string | number | null>[];
}

export type SignOffStep = 'prepare' | 'approve' | 'reopen';

export const reconciliationDashboardService = {
  async summary(month: string, bankAccount?: string): Promise<DashboardSummary> {
    return (await api.get(`${BASE}/summary/`, { params: { month, ...(bankAccount ? { bank_account: bankAccount } : {}) } })).data;
  },
  async queue(name: DashQueue, month: string, bankAccount?: string): Promise<QueueData> {
    return (await api.get(`${BASE}/queue/`, { params: { name, month, ...(bankAccount ? { bank_account: bankAccount } : {}) } })).data;
  },
  async signOff(step: SignOffStep, month: string, bankAccount: string, reason = ''): Promise<SignOff> {
    return (await api.post(`${BASE}/sign-off/`, { action: step, month, bank_account: bankAccount, reason })).data;
  },
  async exportCsv(name: DashTable | DashQueue, month: string, bankAccount?: string): Promise<void> {
    const response = await api.get(`${BASE}/export/`, {
      params: { name, month, ...(bankAccount ? { bank_account: bankAccount } : {}) },
      responseType: 'blob',
    });
    const href = window.URL.createObjectURL(response.data as Blob);
    const link = window.document.createElement('a');
    link.href = href;
    link.download = `reconciliation-${name}-${month}.csv`;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(href);
  },
};
